// hooks/useAuditLogs.jsx
import { useState, useEffect, useCallback } from "react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

/**
 * useAuditLogs — server-side filtered audit log fetcher.
 *
 * @param {Object} [options]
 * @param {Object} [options.filters]  - { changeCategory, changeType, status, search }
 * @param {number} [options.page]     - Current page (1-indexed)
 * @param {number} [options.limit]    - Page size (default 25)
 * @param {string} [options.sort]     - Sort field (default "-createdAt")
 * @param {string} [options.companyId] - Override companyId (for ActivityTab)
 */
export function useAuditLogs({
  filters = {},
  page = 1,
  limit = 25,
  sort = "-createdAt",
  companyId: companyIdOverride,
} = {}) {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const companyId = companyIdOverride || effectiveCompanyId;

  const fetchLogs = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      // Only send companyId when overridden (e.g. ActivityTab viewing a specific company).
      // For the main Audit page, let the backend use req.companyId from CompanyHeader.
      if (companyIdOverride) {
        params.set("companyId", companyIdOverride);
      }
      params.set("page", String(page));
      params.set("limit", String(limit));
      params.set("sort", sort);

      if (filters.changeCategory) params.set("changeCategory", filters.changeCategory);
      if (filters.changeType) params.set("changeType", filters.changeType);
      if (filters.status) params.set("status", filters.status);
      if (filters.search) params.set("search", filters.search);

      const response = await makeApiRequest(
        "get",
        `${apiRoutes.getAuditLogs}?${params.toString()}`,
        { id: auth?.user?.id, accessToken: auth?.user?.accessToken },
        companyId
      );

      // makeApiRequest returns raw Axios response — .data is the JSON body
      const body = response?.data || {};
      setLogs(Array.isArray(body.data) ? body.data : []);
      setPagination(body.pagination || { page: 1, limit: 25, total: 0, totalPages: 0 });
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [companyId, page, limit, sort, filters.changeCategory, filters.changeType, filters.status, filters.search]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return {
    logs,
    pagination,
    loading,
    error,
    refetch: fetchLogs,
    setLogs,
  };
}
